import { IgDot } from "@intelligems/ig-theme/IgDot";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { DataType } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { Stack, useTheme } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";

import { formatMetricValue } from "../../shared/utils/mobileAnalyticsFormatters";

interface MobileLegendItemProps {
  label: string;
  color: string;
  value?: number;
  confidenceInterval?: number;
  dataType?: DataType;
  variant?: "body1" | "body2";
  disabled?: boolean;
  dashed?: boolean;
  onClick?: () => void;
}

export const MobileLegendItem = ({
  label,
  color,
  value,
  confidenceInterval,
  dataType = "number",
  variant = "body1",
  disabled,
  dashed,
  onClick,
}: MobileLegendItemProps) => {
  const theme = useTheme();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const currency = storeCurrencyConfig?.value || {
    code: "USD",
    locale: "en-US",
  };

  return (
    <Stack
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.();
        }
      }}
      direction="row"
      alignItems="center"
      spacing={2.5}
      sx={{
        padding: theme.spacing(2, 0),
        "&:hover": {
          cursor: "pointer",
        },
      }}
    >
      <IgDot
        color={
          dashed
            ? "transparent"
            : disabled
              ? theme.palette.text.disabled
              : color
        }
        size={12}
        sx={{
          border: dashed
            ? `2px dashed ${disabled ? theme.palette.text.disabled : color}`
            : "none",
        }}
      />
      <Stack
        direction="row"
        spacing={0.5}
        justifyContent="space-between"
        alignItems="center"
        sx={{ flex: 1 }}
      >
        <IgTypography
          variant={variant}
          sx={{
            color: disabled
              ? theme.palette.text.disabled
              : theme.palette.text.secondary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "60%",
          }}
        >
          {label}
        </IgTypography>
        <Stack direction="column" alignItems="baseline" spacing={1}>
          <IgTypography
            variant="body2"
            fontWeight={600}
            sx={{ color: theme.palette.text.primary }}
          >
            {formatMetricValue(value, dataType, currency)}
          </IgTypography>
          {confidenceInterval !== undefined && (
            <IgTypography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              ± {formatMetricValue(confidenceInterval, "percent", currency)}
            </IgTypography>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
